import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { TrendingDown, Clock, AlertCircle, Home, Calendar, CalendarCheck, Gavel, Timer, Eye, CheckCircle, X, Bell, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatWithTimezoneLabel, isDeadlinePassed } from "@/lib/timezone";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
interface BidWithProposal {
  amount: number;
  created_at: string;
  proposal_id: string;
  proposals: {
    supplier_company_name: string;
    initial_bid_amount: number;
  } | null;
}

interface RequestedBid {
  id: string;
  amount: number;
  created_at: string;
  status: string;
}

const SupplierBidding = () => {
  const { rfpId, token } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [rfp, setRfp] = useState<any>(null);
  const [proposal, setProposal] = useState<any>(null);
  const [currentBid, setCurrentBid] = useState<number>(0);
  const [lowestBid, setLowestBid] = useState<number>(0);
  const [allBids, setAllBids] = useState<BidWithProposal[]>([]);
  const [rankedSuppliers, setRankedSuppliers] = useState<any[]>([]);
  const [newBidAmount, setNewBidAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestedBid, setRequestedBid] = useState<RequestedBid | null>(null);
  const [respondingToRequest, setRespondingToRequest] = useState(false);
  const [showCongratsDialog, setShowCongratsDialog] = useState(false);

  useEffect(() => {
    fetchData();
    
    if (rfpId) {
      const channel = supabase
        .channel(`bidding-${rfpId}`)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "bids", filter: `rfp_id=eq.${rfpId}` }, 
          () => fetchBids()
        )
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [rfpId, token]);

  const fetchData = async () => {
    try {
      const { data: inv } = await supabase.from("supplier_invitations").select("*").eq("invite_token", token!).single();
      if (!inv) throw new Error("Invalid token");

      const { data: rfpData } = await supabase.from("rfps").select("*").eq("id", rfpId!).single();
      setRfp(rfpData);

      const { data: proposalData } = await supabase.from("proposals").select("*").eq("invitation_id", inv.id).single();
      setProposal(proposalData);

      await fetchBids();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    if (!proposal) return;
    
    // Fetch requested bids for this proposal (status = 'requested')
    const { data: pendingBids } = await supabase
      .from("bids")
      .select("id, amount, created_at, status")
      .eq("proposal_id", proposal.id)
      .eq("status", "requested")
      .order("created_at", { ascending: false })
      .limit(1);
    
    if (pendingBids && pendingBids.length > 0) {
      setRequestedBid(pendingBids[0]);
    } else {
      setRequestedBid(null);
    }
    
    // Get confirmed bids only for current bid calculation
    const { data: myBids } = await supabase
      .from("bids")
      .select("amount")
      .eq("proposal_id", proposal.id)
      .eq("status", "confirmed")
      .order("amount", { ascending: true });
    
    if (myBids && myBids.length > 0) {
      setCurrentBid(myBids[0].amount);
    } else if (proposal.initial_bid_amount) {
      setCurrentBid(proposal.initial_bid_amount);
    } else {
      setCurrentBid(0);
    }

    // Get all proposals for this RFP with their best bids
    const { data: allProposals } = await supabase
      .from("proposals")
      .select("id, supplier_company_name, initial_bid_amount, submitted_at")
      .eq("rfp_id", rfpId!);

    // Get all confirmed bids for this RFP
    const { data: allBidsData } = await supabase
      .from("bids")
      .select("amount, created_at, proposal_id, proposals(supplier_company_name, initial_bid_amount)")
      .eq("rfp_id", rfpId!)
      .eq("status", "confirmed")
      .order("amount", { ascending: true });
    
    if (allBidsData && allBidsData.length > 0) {
      setLowestBid(allBidsData[0].amount);
      setAllBids(allBidsData);
    }

    // Calculate ranked suppliers based on best bid
    const supplierBestBids = new Map<string, { amount: number; company: string; initialBid: number; submittedAt: string }>();
    
    // First add initial bids from proposals
    (allProposals || []).forEach(p => {
      supplierBestBids.set(p.id, {
        amount: p.initial_bid_amount,
        company: p.supplier_company_name,
        initialBid: p.initial_bid_amount,
        submittedAt: p.submitted_at
      });
    });

    // Then update with actual confirmed bids (lower amounts)
    (allBidsData || []).forEach(bid => {
      const existing = supplierBestBids.get(bid.proposal_id);
      if (existing && bid.amount < existing.amount) {
        supplierBestBids.set(bid.proposal_id, {
          ...existing,
          amount: bid.amount
        });
      }
    });

    // Convert to array and sort by best bid
    const ranked = Array.from(supplierBestBids.entries())
      .map(([proposalId, data]) => ({
        proposalId,
        ...data,
        isMe: proposalId === proposal?.id
      }))
      .sort((a, b) => a.amount - b.amount);

    setRankedSuppliers(ranked);
    
    if (ranked.length > 0) {
      setLowestBid(ranked[0].amount);
    }
  };

  const handleAcceptRequest = async () => {
    if (!requestedBid || !proposal || !rfpId) return;
    
    setRespondingToRequest(true);
    try {
      // Update the requested bid to confirmed
      const { error: bidError } = await supabase
        .from("bids")
        .update({ status: "confirmed" })
        .eq("id", requestedBid.id);
      
      if (bidError) throw bidError;

      // Update proposal selection_status to successful
      const { error: proposalError } = await supabase
        .from("proposals")
        .update({ selection_status: "successful" })
        .eq("id", proposal.id);
      
      if (proposalError) throw proposalError;

      // Update RFP status to COMPLETED
      const { error: rfpError } = await supabase
        .from("rfps")
        .update({ status: "COMPLETED" })
        .eq("id", rfpId);

      if (rfpError) throw rfpError;

      // Mark other proposals as unsuccessful
      const { error: otherProposalsError } = await supabase
        .from("proposals")
        .update({ selection_status: "unsuccessful" })
        .eq("rfp_id", rfpId)
        .neq("id", proposal.id);
      
      if (otherProposalsError) console.error("Error updating other proposals:", otherProposalsError);
      
      setRequestedBid(null);
      setShowCongratsDialog(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to accept the offer",
        variant: "destructive",
      });
    } finally {
      setRespondingToRequest(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!requestedBid) return;
    
    setRespondingToRequest(true);
    try {
      // Delete the requested bid (keeps the previous price)
      const { error } = await supabase
        .from("bids")
        .delete()
        .eq("id", requestedBid.id);
      
      if (error) throw error;
      
      toast({
        title: "Offer Rejected",
        description: "The requested offer has been declined. Your previous bid remains active.",
      });
      
      setRequestedBid(null);
      await fetchBids();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to reject the offer",
        variant: "destructive",
      });
    } finally {
      setRespondingToRequest(false);
    }
  };

  useEffect(() => {
    if (proposal) {
      fetchBids();
    }
  }, [proposal]);

  const submitBid = async () => {
    if (!proposal || !newBidAmount) return;

    const bidAmount = parseFloat(newBidAmount);
    if (currentBid > 0 && bidAmount >= currentBid) {
      toast({ title: "Error", description: "New bid must be lower than your current bid", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      await supabase.from("bids").insert([{ rfp_id: rfpId!, proposal_id: proposal.id, amount: bidAmount, source: "AUCTION_BID" }]);
      toast({ title: "Success", description: "Bid placed successfully!" });
      setNewBidAmount("");
      await fetchBids();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  if (!proposal) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-red-500" />
                Proposal Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                You need to submit a proposal before participating in the reverse auction.
              </p>
              <Button onClick={() => window.location.href = `/rfp/${rfpId}/respond/${token}`}>
                Submit Proposal
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const auctionNotStarted = rfp ? !isDeadlinePassed(rfp.auction_start) : true;
  const auctionEnded = rfp ? isDeadlinePassed(rfp.auction_end) : false;
  const isActive = (rfp?.status === "AUCTION_ACTIVE") || (!auctionNotStarted && !auctionEnded && rfp?.status !== "AUCTION_ENDED");
  const hasEnded = rfp?.status === "AUCTION_ENDED" || auctionEnded;

  // Auction must be locked until exact start time
  if (auctionNotStarted && rfp) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-6 w-6 text-blue-500" />
                Auction Not Started Yet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                The auction for this RFP has not started yet. Please return at the scheduled time.
              </p>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600">Auction Starts</p>
                <p className="text-xl font-bold text-blue-700">{formatWithTimezoneLabel(rfp.auction_start)}</p>
              </div>
              <div className="flex gap-4">
                <Button onClick={() => navigate("/supplier/dashboard")}>
                  <Home className="h-4 w-4 mr-2" />
                  Return to Dashboard
                </Button>
                <Button variant="outline" onClick={() => navigate(`/supplier/rfp/${rfpId}/view/${token}`)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Proposal
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Format date for timeline display
  const formatTimelineDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${day} ${month} ${year} – ${displayHours}:${minutes} ${ampm}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <Link to="/supplier/dashboard">
            <Button variant="outline" className="gap-2">
              <Home className="h-4 w-4" />
              Return to Dashboard
            </Button>
          </Link>
          {isActive && (
            <div className="flex items-center gap-2 text-red-600">
              <div className="h-3 w-3 bg-red-600 rounded-full animate-pulse"></div>
              <span className="font-semibold">Live Auction</span>
            </div>
          )}
        </div>

        {/* RFP Title */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl">{rfp?.title}</CardTitle>
            <p className="text-muted-foreground">{rfp?.description}</p>
          </CardHeader>
        </Card>

        {/* Timeline Section */}
        <Card>
          <CardContent className="py-6">
            <div className="grid grid-cols-4 gap-4">
              {/* Request Published */}
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <div className="p-3 rounded-lg bg-blue-50">
                    <Calendar className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                <p className="font-semibold text-sm">Request published</p>
                <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp?.created_at)}</p>
              </div>

              {/* Proposals Due */}
              <div className="text-center relative">
                <div className="absolute top-6 left-0 w-full h-px border-t-2 border-dashed border-blue-200 -z-10" style={{ left: '-50%', width: '100%' }}></div>
                <div className="flex justify-center mb-3">
                  <div className="p-3 rounded-lg bg-blue-50">
                    <CalendarCheck className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                <p className="font-semibold text-sm">Proposals due</p>
                <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp?.submission_deadline)}</p>
              </div>

              {/* Auction Start */}
              <div className="text-center relative">
                <div className="absolute top-6 left-0 w-full h-px border-t-2 border-dashed border-blue-200 -z-10" style={{ left: '-50%', width: '100%' }}></div>
                <div className="flex justify-center mb-3">
                  <div className={`p-3 rounded-lg ${!auctionNotStarted ? 'bg-green-50' : 'bg-blue-50'}`}>
                    <Gavel className={`h-8 w-8 ${!auctionNotStarted ? 'text-green-500' : 'text-blue-500'}`} />
                  </div>
                </div>
                <p className="font-semibold text-sm">Auction start</p>
                <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp?.auction_start)}</p>
                {!auctionNotStarted ? (
                  <Badge className="mt-2 bg-green-500">Started</Badge>
                ) : (
                  <Badge variant="secondary" className="mt-2">Pending</Badge>
                )}
              </div>

              {/* Auction End */}
              <div className="text-center relative">
                <div className="absolute top-6 left-0 w-full h-px border-t-2 border-dashed border-blue-200 -z-10" style={{ left: '-50%', width: '100%' }}></div>
                <div className="flex justify-center mb-3">
                  <div className={`p-3 rounded-lg ${hasEnded ? 'bg-gray-100' : 'bg-blue-50'}`}>
                    <Timer className={`h-8 w-8 ${hasEnded ? 'text-gray-500' : 'text-blue-500'}`} />
                  </div>
                </div>
                <p className="font-semibold text-sm">Auction end</p>
                <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp?.auction_end)}</p>
                {hasEnded && (
                  <Badge variant="secondary" className="mt-2">Completed</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price Rankings */}
        {rankedSuppliers.length > 0 && (
          <Card>
            <CardContent className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {rankedSuppliers.slice(0, 3).map((supplier, index) => (
                  <div key={supplier.proposalId} className="border rounded-lg overflow-hidden">
                    {/* Ranking Header */}
                    <div className="bg-gray-50 py-3 px-4 border-b">
                      <p className="font-bold text-center">Price Ranking: {index + 1}</p>
                    </div>
                    
                    {/* Company Name */}
                    <div className="py-4 px-4 text-center border-b">
                      <p className="font-medium">
                        {supplier.isMe ? `${supplier.company} (You)` : supplier.company}
                      </p>
                    </div>

                    {/* Action Button */}
                    <div className="p-4">
                      {supplier.isMe ? (
                        <Button 
                          className="w-full bg-blue-500 hover:bg-blue-600"
                          onClick={() => navigate(`/supplier/rfp/${rfpId}/view/${token}`)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Your Proposal
                        </Button>
                      ) : (
                        <div className="py-2 text-center text-muted-foreground text-sm">
                          Competitor bid
                        </div>
                      )}
                    </div>

                    {/* Submitted Date */}
                    <div className="py-3 px-4 bg-gray-50 text-center border-t">
                      <p className="text-xs text-muted-foreground">{formatTimelineDate(supplier.submittedAt)}</p>
                    </div>

                    <Separator />

                    {/* Total Price */}
                    <div className="p-4">
                      <p className="font-semibold text-muted-foreground mb-2">Total Price</p>
                      <p className="text-2xl font-bold">${supplier.amount.toLocaleString()} USD</p>
                      <p className="text-sm text-muted-foreground line-through">${supplier.initialBid.toLocaleString()} USD</p>
                      <p className="text-xs text-muted-foreground mt-1">Fixed Fee | USD</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Show more suppliers if there are more than 3 */}
              {rankedSuppliers.length > 3 && (
                <div className="mt-6 pt-6 border-t">
                  <p className="font-semibold mb-4">Other Participants</p>
                  <div className="space-y-2">
                    {rankedSuppliers.slice(3).map((supplier, index) => (
                      <div key={supplier.proposalId} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-muted-foreground">#{index + 4}</span>
                          <span>{supplier.isMe ? `${supplier.company} (You)` : supplier.company}</span>
                        </div>
                        <span className="font-bold">${supplier.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Requested Offer Section */}
        {requestedBid && (
          <Card className="border-2 border-orange-400 bg-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Bell className="h-5 w-5" />
                Requested Offer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-white rounded-lg border border-orange-200">
                <p className="text-sm text-orange-600 mb-1">The buyer has requested you to revise your offer to:</p>
                <p className="text-3xl font-bold text-orange-700">${requestedBid.amount.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Your current bid: ${currentBid.toLocaleString()}
                </p>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleAcceptRequest} 
                  disabled={respondingToRequest}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accept Offer
                </Button>
                <Button 
                  onClick={handleRejectRequest} 
                  disabled={respondingToRequest}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject Offer
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                If you reject, your current bid of ${currentBid.toLocaleString()} will remain active.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Your Bid Status and Input */}
        <Card>
          <CardHeader>
            <CardTitle>Your Bidding</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-sm text-blue-600">Your Current Bid</p>
                <p className="text-2xl font-bold text-blue-700">${currentBid.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-sm text-green-600">Lowest Bid</p>
                <p className="text-2xl font-bold text-green-700">${lowestBid.toLocaleString()}</p>
              </div>
            </div>
            
            {isActive ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="Enter bid amount" 
                    value={newBidAmount} 
                    onChange={(e) => setNewBidAmount(e.target.value)} 
                    className="flex-1"
                  />
                  <Button onClick={submitBid} disabled={submitting} className="gap-2">
                    <TrendingDown className="h-4 w-4" />
                    Place Bid
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your new bid must be lower than your current bid of ${currentBid.toLocaleString()}
                </p>
              </div>
            ) : auctionNotStarted && rfp ? (
              <div className="text-center py-6">
                <Clock className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <p className="font-medium">Auction starts: {formatWithTimezoneLabel(rfp.auction_start)}</p>
              </div>
            ) : (
              <div className="text-center py-6">
                <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="font-medium">Auction has ended</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Congratulations Dialog */}
      <AlertDialog open={showCongratsDialog} onOpenChange={setShowCongratsDialog}>
        <AlertDialogContent className="text-center">
          <AlertDialogHeader>
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 p-4 rounded-full">
                <Trophy className="h-12 w-12 text-green-600" />
              </div>
            </div>
            <AlertDialogTitle className="text-2xl text-center">
              Congratulations!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-lg">
              You have been selected as the winning bidder for this RFP. The buyer will be in contact with you shortly.
              <span className="block mt-3 text-sm text-muted-foreground">
                You can find this and other completed RFPs in the Closed Auctions section.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            <Button 
              onClick={() => {
                setShowCongratsDialog(false);
                navigate("/supplier/closed-auctions");
              }}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Go to Closed Auctions
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setShowCongratsDialog(false);
                navigate("/supplier/dashboard");
              }}
              className="w-full"
            >
              Return to Dashboard
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SupplierBidding;
